import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";

export interface TidePrediction {
  time: Date;
  height: number; // meters (convert at render via `convertHeight`)
  type: "H" | "L"; // High or Low
}

export interface TideHourly {
  time: Date;
  height: number; // meters (convert at render via `convertHeight`)
}

interface TideApiResponse {
  stationId: string | null;
  stationName: string | null;
  stationLat?: number | null;
  stationLon?: number | null;
  stationTimezone?: string | null;
  stationDistanceKm?: number | null;
  stationSource?: "region" | "nearest" | null;
  predictions: Array<{
    t: string; // "2024-01-15 06:23"
    v: string; // "4.567"
    type: "H" | "L";
  }>;
  hourly: Array<{
    t: string;
    v: string;
  }>;
}

export interface TideData {
  stationId: string | null;
  stationName: string;
  stationLat: number | null;
  stationLon: number | null;
  stationTimezone: string | null;
  stationDistanceKm: number | null;
  stationSource: "region" | "nearest" | null;
  predictions: TidePrediction[];
  hourly: TideHourly[];
}

// Stored tide constituents (api/tide-stations.json) have amplitudes in
// metres, so @neaps/tide-predictor emits levels in metres. Keep the
// canonical representation in metres; consumers format per user units.
export function tideDataFromApi(
  data: TideApiResponse | null | undefined,
): TideData | null {
  if (!data) return null;
  return {
    stationId: data.stationId ?? null,
    stationName: data.stationName ?? "Unknown",
    stationLat: data.stationLat ?? null,
    stationLon: data.stationLon ?? null,
    stationTimezone: data.stationTimezone ?? null,
    stationDistanceKm: data.stationDistanceKm ?? null,
    stationSource: data.stationSource ?? null,
    predictions: (Array.isArray(data.predictions) ? data.predictions : []).map((p) => ({
      time: new Date(p.t),
      height: parseFloat(p.v),
      type: p.type,
    })),
    hourly: (Array.isArray(data.hourly) ? data.hourly : []).map((p) => ({
      time: new Date(p.t),
      height: parseFloat(p.v),
    })),
  };
}

/**
 * Tide height and trend at an arbitrary time, interpolated between the two
 * neighboring samples. Null when fewer than two samples exist. This is the
 * same sample the forecast chart uses.
 */
export function tideAtTime(
  tideData: TideData | undefined,
  targetIso: string,
): { height: number; trend: "rising" | "falling" | "steady" } | null {
  if (!tideData || tideData.hourly.length < 2) return null;
  const t = parseISO(targetIso).getTime();
  const samples = tideData.hourly;

  let before = samples[0];
  let after = samples[samples.length - 1];
  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i].time.getTime();
    const b = samples[i + 1].time.getTime();
    if (a <= t && b >= t) {
      before = samples[i];
      after = samples[i + 1];
      break;
    }
  }
  const a = before.time.getTime();
  const b = after.time.getTime();
  if (b <= a) return { height: before.height, trend: "steady" };
  const frac = Math.min(1, Math.max(0, (t - a) / (b - a)));
  const height = before.height + (after.height - before.height) * frac;
  const delta = after.height - before.height;
  const trend = delta > 0.05 ? "rising" : delta < -0.05 ? "falling" : "steady";
  return { height, trend };
}

async function fetchTidePredictions(
  lat: number,
  lon: number,
  region?: string | null,
): Promise<TideData> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
  });
  if (region) params.set("region", region);

  const response = await fetch(`/api/tides?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Failed to fetch tide data");
  }

  const data: TideApiResponse = await response.json();
  const parsed = tideDataFromApi(data);
  if (!parsed) {
    throw new Error("Failed to fetch tide data");
  }
  return parsed;
}

export function useTideData(
  lat: number | null,
  lon: number | null,
  region?: string | null,
) {
  return useQuery({
    queryKey: ["tides", lat, lon, region ?? null, format(new Date(), "yyyyMMdd")],
    queryFn: () => fetchTidePredictions(lat!, lon!, region),
    enabled: lat != null && lon != null,
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000,
    retry: 2,
  });
}

export function getNextTides(
  predictions: TidePrediction[] | undefined,
  count = 4,
): TidePrediction[] {
  if (!predictions) return [];

  const now = new Date();
  return predictions.filter((p) => p.time > now).slice(0, count);
}
